import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import { Combobox, Field, Option } from "@fluentui/react-components";
import { useQuery } from "@tanstack/react-query";
import { isEqual } from "lodash";
import { useEffect, useState } from "react";

import { teamOfficeGet, teamsGet } from "../../../../services/api.service.js";

interface ChannelWithName {
  id: string;
  msChannelId: string;
  name: string;
}

interface SelectChannelProps {
  // TODO: Fix this with form composition later on
  // Lint exception: FormApi has complex generic signature, using any for flexibility
  field: any;
  msTeamId: string | null;
  label: string;
}

export const SelectChannel = ({
  field,
  msTeamId,
  label = "Room/Table",
}: SelectChannelProps) => {
  const [channels, setChannels] = useState<ChannelWithName[]>([]);
  const { isLoading: isTeamsLoading, error: teamsError } = useQuery({
    queryFn: () => teamsGet({}),
    queryKey: ["teamsGet"],
  });
  const {
    data: office,
    isLoading: isOfficeLoading,
    error: officeError,
  } = useQuery({
    enabled: Boolean(msTeamId),
    queryFn: () => {
      if (!msTeamId) {
        throw new Error("Team ID is required");
      }

      return teamOfficeGet({
        msTeamId,
      });
    },
    queryKey: ["getTeam", msTeamId],
  });
  const isLoading = isTeamsLoading || isOfficeLoading;
  const error = teamsError ?? officeError;

  useEffect(() => {
    if (office) {
      const channelsWithNames: ChannelWithName[] = office.channels.map((ch) => {
        const table = office.tables.find(
          (t) => t.msChannelId === ch.msChannelId
        );
        return { ...ch, name: table?.name ?? ch.msChannelId };
      });

      const sortedChannels = channelsWithNames.sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      setChannels((previousChannels) =>
        isEqual(previousChannels, sortedChannels)
          ? previousChannels
          : sortedChannels
      );
    }
  }, [office]);

  const comboboxPlaceholder = () => {
    if (isLoading) {
      return "Loading rooms...";
    }

    if (!msTeamId) {
      return "Select an office first";
    }

    if (!channels || channels.length === 0) {
      return "No rooms available";
    }

    return "Select a table or room";
  };

  // Get validation state from TanStack Form
  const fieldState = field.state;
  const hasError = fieldState.meta.errors.length > 0;
  const validationMessage = fieldState.meta.errors[0];
  const handleOptionSelect109 = useStableCallback((_, data) => {
    field.handleChange(data.optionValue ?? null);
  });

  return (
    <Field
      label={label}
      required={true}
      validationMessage={validationMessage || error}
      validationState={hasError || error ? "error" : "none"}
    >
      <Combobox
        autoComplete="off"
        className="w-full"
        disabled={
          !(msTeamId && channels) ||
          channels.length === 0 ||
          isLoading ||
          error !== null
        }
        onOptionSelect={handleOptionSelect109}
        placeholder={comboboxPlaceholder()}
        value={channels.find((c) => c.id === field.state.value)?.name || ""}
      >
        {channels.map((channel) => (
          <Option key={channel.id} value={channel.id}>
            {channel.name}
          </Option>
        ))}
      </Combobox>
    </Field>
  );
};
